import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '../icon/icons';

export type ErrorPageVariant = 'not-found' | 'coming-soon' | 'error';

const DEFAULTS: Record<
  ErrorPageVariant,
  { icon: IconName; title: string; text: string; testId: string }
> = {
  'not-found': {
    icon: 'map-pin',
    title: 'Pagina non trovata',
    text: "L'indirizzo non corrisponde a nessuna pagina di B.Site. Controlla il collegamento o torna alla Dashboard.",
    testId: 'not-found-page',
  },
  'coming-soon': {
    icon: 'hourglass',
    title: 'In arrivo',
    text: 'Questa funzione non è ancora disponibile: arriverà con i prossimi rilasci.',
    testId: 'coming-soon-page',
  },
  error: {
    icon: 'warning',
    title: 'Qualcosa non ha funzionato',
    text: 'Non è stato possibile mostrare la pagina. Riprova tra qualche istante.',
    testId: 'error-page',
  },
};

/** Pagina di stato dentro la struttura: non trovata, in arrivo o errore generico (FR-030). */
@Component({
  selector: 'bs-error-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, IconComponent],
  host: { class: 'block' },
  template: `
    <section
      class="mx-auto flex max-w-xl flex-col items-center gap-4 px-4 py-16 text-center"
      [attr.data-testid]="defaults().testId"
    >
      <span
        class="inline-flex size-14 items-center justify-center rounded-2xl bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300"
      >
        <bs-icon [name]="defaults().icon" [size]="28" />
      </span>
      <h1 class="font-display text-4xl font-bold text-ink dark:text-stone-50">
        {{ title() ?? defaults().title }}
      </h1>
      <p class="text-base text-stone-600 dark:text-stone-300">{{ text() ?? defaults().text }}</p>
      <div class="mt-2 flex flex-wrap justify-center gap-2">
        <ng-content />
        @if (homeLink(); as link) {
          <bs-button [link]="link" variant="secondary" icon="arrow-left" testId="error-page-home">
            {{ homeLabel() }}
          </bs-button>
        }
      </div>
    </section>
  `,
})
export class ErrorPageComponent {
  readonly variant = input<ErrorPageVariant>('not-found');
  readonly title = input<string | null>(null);
  readonly text = input<string | null>(null);
  readonly homeLink = input<string | null>('/');
  readonly homeLabel = input('Torna alla Dashboard');

  protected readonly defaults = computed(() => DEFAULTS[this.variant()]);
}
